import React, { useContext } from "react"
import dayjs, { Dayjs } from 'dayjs'
import _ from 'lodash'
import { Timeline, Card, Button, Typography, Divider, Radio, Space, Empty, Tooltip } from "antd"
import {
    FaCircle,
    FaTaxi,
    FaRegClock,
    FaChevronLeft,
    FaChevronRight,
    FaMapMarkerAlt,
    FaCalendarAlt,
    FaTicketAlt,
    FaCalendar,
    FaCar,
    FaChair,
} from "react-icons/fa"
import language from "../../resources/languages/fa_IR"
import { ActionType, API, Enum } from "../../resources/constants"

// Contexts
import GlobalContext from "../../contexts/global"
import AxiosContext from "../../contexts/axios"

// Interfaces
import { AxiosError, AxiosResponsePaginate } from "../../interfaces/axios"
import { Origin, Destination, Location } from "../../interfaces/travel"

const currency = new Intl.NumberFormat()
const { SET_ORIGIN, SET_ORIGIN_LOCATION, SET_DESTINATION, SET_DESTINATION_LOCATION, SET_OPEN_DRAWER, SET_FORM, SET_DIRECTIONS, SET_TRAVEL, SET_TRAVEL_TYPE } = ActionType
const { Form, TravelType, TravelMod } = Enum
const { TRAVEL_NESHAN_DIRECTION, TRAVEL_TRAVEL_PAGINATE, TRAVEL_TRAVEL_MY_PAGINATE } = API
const { Components: { Travel }, Enum: EnumLanguage } = language

export default function Index() {

    const { travel, travelMod, travelType, filter, user, dispatch } = useContext(GlobalContext)
    const { axios } = useContext(AxiosContext)

    const getTravel = (page: number) => axios.post(travelMod === TravelMod.NEW ? TRAVEL_TRAVEL_PAGINATE : TRAVEL_TRAVEL_MY_PAGINATE, filter, { params: { page, limit: 1 }}).then((response: AxiosResponsePaginate) => {
        const { data, data: { docs } } = response
        const travel =  docs.length ? { ...docs[0], paginate: { ...data, docs: null } } : {}
        dispatch({ type: SET_TRAVEL , payload: { travel }})
        resetDirections()
    }).catch( (error: AxiosError) => {
        const { response: { data } } = error;
        dispatch({ type: SET_TRAVEL , payload: { travel: {} }})
        resetDirections()
        // @ts-ignore
        message.error(MessageLanguage[Message.ERROR_REQUEST]).then(response => response)
    })

    const getNeshanDirection = (origin: Origin, originLocation: Location, destination: Destination, destinationLocation: Location) => axios.post(TRAVEL_NESHAN_DIRECTION, {
        type: 'car',
        traffic: false,
        origin: originLocation,
        destination: destinationLocation,
    }).then((res: { data: { routes: { legs: any }[] } }) => {
        const { data: { routes = [] } } = res
        dispatch({ type: SET_DESTINATION , payload: { destination }})
        dispatch({ type: SET_DESTINATION_LOCATION , payload: { destinationLocation }})
        dispatch({ type: SET_ORIGIN , payload: { origin }})
        dispatch({ type: SET_ORIGIN_LOCATION , payload: { originLocation }})
        dispatch({ type: SET_DIRECTIONS , payload: { directions: { routes } }})
    }).catch( (error: AxiosError) => {
        const { response: { data } } = error;
        // @ts-ignore
        message.error(MessageLanguage[Message.ERROR_REQUEST]).then(response => response)
    })

    const resetDirections = () => {
        dispatch({ type: SET_DESTINATION , payload: { destination: null }})
        dispatch({ type: SET_DESTINATION_LOCATION , payload: { destinationLocation: null }})
        dispatch({ type: SET_ORIGIN , payload: { origin: null }})
        dispatch({ type: SET_ORIGIN_LOCATION , payload: { originLocation: null }})
        dispatch({ type: SET_DIRECTIONS , payload: { directions: { routes: [] } }})
    }

    const getBooking = () => {
        dispatch({ type: SET_FORM , payload: { form: user ? Form.BOOKING : Form.SIGN_IN }})
        dispatch({ type: SET_OPEN_DRAWER , payload: { openDrawer: true }})
    }

    const getTicket = () => {
        dispatch({ type: SET_FORM , payload: { form: Form.TICKET }})
        dispatch({ type: SET_OPEN_DRAWER , payload: { openDrawer: true }})
    }

    if (travel && !_.isEmpty(travel)) {
        // @ts-ignore
        const { origin, originLocation, destination, destinationLocation, date, duration, car, seat, number: code, travelType: travelBookingType, personPrice = 0, closePrice = 0, homePrice = 0, paginate: { hasPrevPage = false, prevPage, hasNextPage = false, nextPage } } = travel
        const travelPrice = [
            {
                name: TravelType.Person,
                price: personPrice,
            },
            {
                name: TravelType.Close,
                price: closePrice,
            },
            {
                name: TravelType.Home,
                price: homePrice,
            },
        ].filter(({ name }) => name === travelType)
        return (
            <Card className={'travel-card'} bordered={false}
                  actions={[
                      <Button className={'icon-center'} type="primary" disabled={!hasPrevPage} ghost onClick={() => getTravel(prevPage)}><FaChevronRight /><span>{Travel.prev}</span></Button>,
                      <Button type="primary" className={'icon-center'} ghost onClick={() => getNeshanDirection(origin, originLocation, destination, destinationLocation)}><FaMapMarkerAlt/><span style={{marginRight:5}}>{Travel.direction}</span></Button>,
                      (travelMod === TravelMod.MY) ? <Button type="primary" className={'icon-center'} onClick={() => getTicket()}><FaTicketAlt /><span style={{marginRight:5}}>{Travel.ticket}</span></Button> : <Button type="primary" className={'icon-center'} onClick={() => getBooking()}><FaCalendarAlt /><span style={{marginRight:5}}>{Travel.booking}</span></Button>,
                      <Button className={'icon-center'} type="primary" disabled={!hasNextPage} ghost onClick={() => getTravel(nextPage)}><span>{Travel.next}</span><FaChevronLeft /></Button>
                  ]}
            >
                <div className={'travel-number'}><u>{code}</u></div>
                <Timeline
                    mode={'alternate'}
                    className={'travel-timeline'}
                    items={[
                        {
                            dot: <FaTaxi />,
                            label: dayjs(date).format('HH:mm'),
                            children: <><Typography.Text strong>{origin.name}</Typography.Text> <Typography.Text style={{fontSize: 11}}>({Travel.origin})</Typography.Text></>,
                        },
                        {
                            dot: <FaRegClock />,
                            children: [duration.split(':')[0], duration.split(':')[1]].join(':'),
                            label: <Typography.Text style={{fontSize: 11}}>{Travel.duration}</Typography.Text>,
                        },
                        {
                            dot: <FaCircle size={8} />,
                            label: dayjs(date).add(Number(duration.split(':')[0]), 'hours').add(Number(duration.split(':')[1]), 'minutes').format('HH:mm'),
                            children: <><Typography.Text strong>{destination.name}</Typography.Text> <Typography.Text style={{fontSize: 11}}>({Travel.destination})</Typography.Text></>,
                        },
                    ]}
                />
                <Divider className={'travel-divider'} />
                <div className={'travel-description'}>
                    <Space direction={'vertical'}>
                        <Space>
                            <FaCalendar />
                            <Typography.Text>{Travel.date}:</Typography.Text>
                            <Typography.Text strong>{dayjs(date).format('YYYY/MM/DD')}</Typography.Text>
                        </Space>
                        <Space>
                            <FaCar />
                            <Typography.Text>{Travel.car}:</Typography.Text>
                            <Typography.Text strong>{car.name}</Typography.Text>
                        </Space>
                        <Space>
                            <FaChair />
                            <Typography.Text>{Travel.seat}:</Typography.Text>
                            <Typography.Text strong>{seat}</Typography.Text>
                        </Space>
                    </Space>
                    <Radio.Group defaultValue={travelType} onChange={({ target: { value: travelType }}) => dispatch({ type: SET_TRAVEL_TYPE , payload: { travelType }})}>
                    <Space direction={'vertical'}>
                        <Tooltip open={TravelType.Person === travelType} placement="left" overlayClassName={'travel-type-tooltip'} overlayStyle={{width:'50px'}} title={<>{`${Travel.travelTypeDescriptionOne} ${Travel.travelTypeDescriptionThree}`}<br/>{`${Travel.travelTypeDescriptionTwo} ${Travel.travelTypeDescriptionThree}`}</>}><Radio value={TravelType.Person} disabled={!personPrice || (Boolean(travelBookingType) && (TravelMod.MY === travelMod))}><span className={'travel-type-title'}>{EnumLanguage.TravelType[TravelType.Person]}</span></Radio></Tooltip>
                        <Tooltip open={TravelType.Close === travelType} placement="left" overlayClassName={'travel-type-tooltip'} overlayStyle={{width:'50px'}} title={<>{`${Travel.travelTypeDescriptionOne} ${Travel.travelTypeDescriptionThree}`}<br/>{`${Travel.travelTypeDescriptionTwo} ${Travel.travelTypeDescriptionThree}`}</>}><Radio value={TravelType.Close} disabled={!closePrice || Boolean(travelBookingType)}><span className={'travel-type-title'}>{EnumLanguage.TravelType[TravelType.Close]}</span></Radio></Tooltip>
                        <Tooltip open={TravelType.Home === travelType} placement="right" overlayClassName={'travel-type-tooltip'} overlayStyle={{width:'60px'}} title={<>{`${Travel.travelTypeDescriptionOne} ${Travel.travelTypeDescriptionFour}`}<br/>{`${Travel.travelTypeDescriptionTwo} ${Travel.travelTypeDescriptionFour}`}</>}><Radio value={TravelType.Home} disabled={!homePrice || Boolean(travelBookingType)}><span className={'travel-type-title'}>{EnumLanguage.TravelType[TravelType.Home]}</span></Radio></Tooltip>
                    </Space>
                    </Radio.Group>
                </div>
                <Divider className={'travel-price'}>
                    <Space>
                        <Typography.Text strong>{travelPrice.length ? currency.format(travelPrice[0].price/10) : 0}</Typography.Text>
                        <Typography.Text>{Travel.toman}</Typography.Text>
                    </Space>
                </Divider>
            </Card>
        )
    } else return <Card className={'travel-card travel-empty'} bordered={false}>
        <Space direction="vertical" style={{width:'100%'}}>
            <Empty description={travelMod === TravelMod.MY ? Travel.emptyMy : Travel.emptyNew} />
            <Button block type="primary" onClick={() => {
                dispatch({ type: SET_FORM , payload: { form: user ? Form.REQUEST : Form.SIGN_IN }})
                dispatch({ type: SET_OPEN_DRAWER , payload: { openDrawer: true }})
            }}>{ Travel.request }</Button>
            <Button block style={{marginBottom:'30px'}} onClick={() => {
                dispatch({ type: SET_FORM , payload: { form: Form.FILTER }})
                dispatch({ type: SET_OPEN_DRAWER , payload: { openDrawer: true }})
            }}>{ Travel.filter }</Button>
        </Space>
    </Card>
}
